import React, { createContext, useState, useContext, useEffect } from 'react';
import { responseEmitter } from '../usb/USBConnector';
import { checkPMConnection } from '../usb/USBConnector';

const ConnectionContext = createContext();

export const ConnectionProvider = ({ children }) => {
	const [isConnected, setIsConnected] = useState(false);
	const [checkingPMConnection, setCheckingPMConnection] = useState(false);
	const [isPBM, setIsPBM] = useState(
		() => localStorage.getItem('connectedDeviceType') === 'PBM'
	);
	const [serialNumber, setSerialNumber] = useState(
		() => isPBM ? localStorage.getItem('PMSerialNumber')
		: localStorage.getItem('analyzerSerialNumber') ?? 'N/A'
	);
	const [pbmSerialNumber, setPbmSerialNumber] = useState(
		() => localStorage.getItem('PBMSerialNumber') ?? null
	);
	const [firmwareNumber, setFirmwareNumber] = useState(
		() => localStorage.getItem('analyzerFirmwareVersion') ?? 'N/A'
	);
	/**
	 * Every 10 seconds will check to see if the PM is connected to the PBM by requesting PM version & date info.
	 * @param {*} depth the current recursive depth of the function, only used when an Anaylzer is connected to stop the recursion
	 * @returns Handled in listenToPort function in USBConnector, listens for no response and will break connection & display a toast
	 */
	const isPMConnected = async (depth = 0) => {
        if (depth >= 5) return;  // try 5 times before giving up
        if (depth === 0) // inital delay to allow localStorage to be set
            await new Promise(resolve => setTimeout(resolve, 5000));

        const currentIsPBM = localStorage.getItem('connectedDeviceType') === 'PBM';
        setIsPBM(currentIsPBM);

        if (currentIsPBM) {
            await checkPMConnection();
            setTimeout(() => isPMConnected(1), 10000);
            return;
        } else {
            setTimeout(() => isPMConnected(depth + 1), 3000);
        }
    };

	useEffect(() => {
		const handleConnection = (status) => {
			setIsConnected(status);
			// Attach PM heartbeat
			if(!checkingPMConnection)
				isPMConnected();
			setCheckingPMConnection(true);
		};
		const handleDeviceInfo = (payload) => {
			const match = payload.match(/PM\d{5}/);
			if (match) {
				localStorage.setItem('PMSerialNumber', match[0]);
				localStorage.setItem('connectedDeviceType', 'PBM');
				setSerialNumber(match[0]);
				setIsPBM(true);
			}
		}
		const handlePBMID = (payload) => {
			const trimmed = payload.replace(/[\r\n]+$/, '');
			const match = trimmed.slice(3, trimmed.length - 2)
			if (match) {
				localStorage.setItem('PBMSerialNumber', 'PBM1' +match);
				localStorage.setItem('connectedDeviceType', 'PBM');
				setPbmSerialNumber(match);
				setIsPBM(true);
			}
		}

		responseEmitter.on('connection', handleConnection);
		responseEmitter.on('data', handleDeviceInfo);
		responseEmitter.on('PBMID', handlePBMID);
		return () => {
			responseEmitter.off('connection', handleConnection);
			responseEmitter.off('data', handleDeviceInfo);
			responseEmitter.off('PBMID', handlePBMID);
		};
	}, []);

	return (
		<ConnectionContext.Provider
			value={{
				isConnected,
				setIsConnected,
				isPBM,
				serialNumber,
				pbmSerialNumber,
				firmwareNumber
			}}
		>
			{children}
		</ConnectionContext.Provider>
	);
};

export const useConnection = () => {
	return useContext(ConnectionContext);
};